import React, { useEffect, useState } from 'react';
import { Mail, Check, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';

const SITE_URL = 'https://www.workbees.space';

interface NewsPostItem {
  slug: string;
  title: string;
  date: string;
  description?: string;
  tags?: string;
  pipeline?: string;
}

/** 栏目小标签（compute-pricing 琥珀 / deep-news 靛蓝） */
function PipelineChip({ pipeline }: { pipeline?: string }) {
  if (!pipeline) return null;
  const isPricing = pipeline === 'compute-pricing';
  return (
    <span
      className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
        isPricing
          ? 'bg-amber-50 text-amber-700 border border-amber-200'
          : 'bg-indigo-50 text-indigo-600 border border-indigo-100'
      }`}
    >
      {isPricing ? '算力价格' : '深度报道'}
    </span>
  );
}

/** 订阅卡片：邮箱输入 + 订阅按钮（成功打勾反馈） */
function SubscribeCard() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function subscribe() {
    if (!email.trim() || state === 'loading') return;
    setState('loading');
    setErrorMsg('');
    try {
      const resp = await fetch('/api/news/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setState('success');
      } else {
        setState('error');
        setErrorMsg(data.error || '订阅失败，请稍后重试');
      }
    } catch {
      setState('error');
      setErrorMsg('网络异常，请稍后重试');
    }
  }

  return (
    <div className="mb-8 p-6 rounded-2xl bg-slate-50 border border-slate-100">
      <div className="flex items-center gap-2 mb-2">
        <Mail size={16} className="text-indigo-600" />
        <span className="text-sm font-semibold text-slate-800">订阅到邮箱</span>
      </div>
      {state === 'success' ? (
        <div className="flex items-center gap-2 text-sm text-emerald-600">
          <Check size={16} />
          订阅成功，欢迎邮件已发出
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (state === 'error') setState('idle'); }}
              onKeyDown={(e) => e.key === 'Enter' && subscribe()}
              placeholder="输入邮箱地址"
              className="flex-1 min-w-0 px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white text-slate-700 placeholder-slate-300 focus:outline-none focus:border-indigo-400"
            />
            <button
              onClick={subscribe}
              disabled={state === 'loading' || !email.trim()}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 transition-colors"
            >
              {state === 'loading' ? <Loader2 size={16} className="animate-spin" /> : '订阅'}
            </button>
          </div>
          {state === 'error' && <p className="mt-2 text-xs text-red-500">{errorMsg}</p>}
        </>
      )}
    </div>
  );
}

const Blog: React.FC = () => {
  const [posts, setPosts] = useState<NewsPostItem[] | null>(null);
  const [loadError, setLoadError] = useState(false);

  const loadPosts = () => {
    setLoadError(false);
    fetch('/api/news')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => setPosts(Array.isArray(data) ? data : []))
      .catch(() => setLoadError(true));
  };

  useEffect(() => {
    document.title = 'AI NEWS | Teegal';
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', `${SITE_URL}/blog`);
    loadPosts();
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <Navbar />

      <header className="pt-28 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">⚡</span>
            <span className="text-xs font-mono text-slate-400 tracking-wider">TEEGAL出品</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-2">NEWS频道</h1>
        </div>
      </header>

      <main className="pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <SubscribeCard />

          {posts === null && !loadError && (
            <p className="text-slate-400 text-center py-20 text-sm">加载中...</p>
          )}
          {loadError && (
            <div className="text-center py-20">
              <p className="text-slate-400 mb-4 text-sm">加载失败</p>
              <button onClick={loadPosts} className="text-sm text-indigo-600 hover:underline">
                重试
              </button>
            </div>
          )}
          {posts !== null && posts.length === 0 && !loadError && (
            <p className="text-slate-400 text-center py-20">还没有文章</p>
          )}

          {/* 按日期分组时间线 */}
          {posts && posts.length > 0 && (
            <div className="space-y-8">
              {Object.entries(
                posts.reduce<Record<string, NewsPostItem[]>>((acc, p) => {
                  (acc[p.date] ||= []).push(p);
                  return acc;
                }, {}),
              ).map(([date, group]) => (
                <div key={date}>
                  {/* 日期分隔线 */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                      {date}
                    </span>
                    <div className="flex-1 h-px bg-slate-100" />
                    <span className="text-[11px] text-slate-300">{group.length} 篇</span>
                  </div>
                  {/* 文章卡片 */}
                  <div className="space-y-3">
                    {group.map((post) => (
                      <Link
                        key={post.slug}
                        to={`/blog/${post.slug}`}
                        className="block p-5 rounded-xl bg-white border border-slate-100 hover:border-indigo-200 hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <PipelineChip pipeline={post.pipeline} />
                          {post.tags && (
                            <span className="text-[11px] text-slate-300">
                              {post.tags.split(',').filter(Boolean).slice(0, 3).map(t => `#${t.trim()}`).join(' ')}
                            </span>
                          )}
                        </div>
                        <h2 className="text-base font-semibold text-slate-800 leading-snug mb-1.5 group-hover:text-indigo-600">
                          {post.title}
                        </h2>
                        {post.description && (
                          <p className="text-sm text-slate-500 leading-relaxed line-clamp-2">{post.description}</p>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Blog;
